import { register } from 'register-service-worker';
import { Notify } from 'quasar';
import { mdiRefresh, mdiCloudDownload } from '@quasar/extras/mdi-v7';
import {
  clearAllStorage,
  checkVersionAndClearIfNeeded,
  checkDeployedVersionAndClearIfNeeded,
} from 'src/shared/utilities/versionManager';
import { usePWAUpdate } from 'src/shared/composables/usePWAUpdate';

// The ready(), registered(), cached(), updatefound() and updated()
// events passes a ServiceWorkerRegistration instance in their arguments.
// ServiceWorkerRegistration: https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration

// Allow query flags to help diagnose service worker issues on mobile devices.
// Usage examples (open these URLs once on your device):
//  - ?clear-sw=1 : Unregister all SWs, clear caches, then reload clean
//  - ?no-sw=1   : Skip registering SW for this session (debugging)
const params = new URLSearchParams(window.location.search);

async function clearServiceWorkersAndCaches() {
  try {
    await clearAllStorage({
      clearServiceWorkers: true,
      clearCaches: true,
      clearIndexedDB: true,
      clearLocalStorage: false, // Keep auth token
      clearSessionStorage: true,
    });
  } catch (e) {
    console.warn('clearServiceWorkersAndCaches error', e);
  } finally {
    // Reload without query to avoid re-triggering
    const { origin, pathname } = window.location;
    window.location.replace(`${origin}${pathname}`);
  }
}

if (params.has('clear-sw')) {
  void clearServiceWorkersAndCaches();
} else if (!params.has('no-sw')) {
  // Check deployed version first (network), then local bundle version fallback.
  checkDeployedVersionAndClearIfNeeded()
    .then((deployedCheck) => {
      if (deployedCheck.wasCleared) {
        return true;
      }

      if (!deployedCheck.didCheck) {
        return checkVersionAndClearIfNeeded();
      }

      return false;
    })
    .then((wasCleared) => {
      if (wasCleared) {
        console.log(
          'Storage cleared due to version change. Reloading to apply changes...',
        );
        Notify.create({
          message: 'App updated! Reloading to apply changes...',
          color: 'info',
          icon: mdiRefresh,
          position: 'top',
          timeout: 2000,
        });
        // Reload after a short delay to show the notification
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return; // Don't register SW yet, will register after reload
      }

      // Normal registration if no version change
      registerServiceWorker();
    })
    .catch((err) => {
      console.error('Version check failed:', err);
      // Continue with SW registration anyway
      registerServiceWorker();
    });
}

function registerServiceWorker() {
  register(import.meta.env.QUASAR_SERVICE_WORKER_FILE, {
    // The registrationOptions object will be passed as the second argument
    // to ServiceWorkerContainer.register()
    // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/register#Parameter

    // registrationOptions: { scope: './' },

    ready(/* registration */) {
      console.log('Service worker is active.');
    },

    registered(/* registration */) {
      console.log('Service worker has been registered.');
    },

    cached(/* registration */) {
      console.log('Content has been cached for offline use.');
    },

    updatefound(/* registration */) {
      console.log('New content is downloading.');

      Notify.create({
        message: 'Downloading update...',
        color: 'info',
        icon: mdiCloudDownload,
        position: 'top',
        timeout: 2000,
      });
    },

    updated(registration) {
      console.log('New content is available; please refresh.');

      // Set update available state for persistent UI indicator
      const { setUpdateAvailable, reloadApp } = usePWAUpdate();
      setUpdateAvailable(registration);

      Notify.create({
        message: 'New version available!',
        caption: 'Tap to reload and get the latest features',
        color: 'positive',
        icon: mdiRefresh,
        position: 'top',
        timeout: 0, // Don't auto-dismiss
        actions: [
          {
            label: 'Reload',
            color: 'white',
            handler: () => {
              reloadApp();
            },
          },
          {
            label: 'Later',
            color: 'white',
            // Don't clear the update state - keep it for persistent UI
          },
        ],
      });
    },

    offline() {
      console.log(
        'No internet connection found. App is running in offline mode.',
      );
    },

    error(err) {
      console.error('Error during service worker registration:', err);
    },
  });

  // Start periodic update checking every 2 minutes
  setInterval(
    () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CHECK_UPDATE',
        });
      }
    },
    2 * 60 * 1000,
  ); // 2 minutes

  // SW update handling (single block)
  if ('serviceWorker' in navigator) {
    let refreshing = false;

    // NOTE: no eager SKIP_WAITING post here. Promoting the waiting worker as
    // soon as it appears is what made the "New version available" prompt
    // cosmetic — the page reloaded before the user could answer it.
    // usePWAUpdate().reloadApp() sends that message when the user opts in.

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      navigator.serviceWorker.ready.then((reg) => reg.update()).catch(() => {});
    });
  }
}
